import { landmineNames } from '@mm/constants/landmine-names';
import modalActions, {
  OpenBillingModalParams,
} from '@mm/state/modules/modalsState/actions';
import {
  CallToActionType,
  CampaignDisplayType,
} from '@mm/state/modules/modalsState/props';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
  activeMessagesStatisticSelector,
  selectPlansAllotments,
  selectAnnualPlansAllotments,
} from '@mm/state/selectors/facebookPageSelector';
import { mapPlanAllotmentOptions } from '@mm/state/helpers/billing-info-helpers';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  defaultProPlanSelector,
  enhancedPlansSelector,
} from '@mm/state/selectors/subscriptionSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { RadioInput, Line, Heading, Link } from '@mm/components';
import { CloseCircleIcon } from '@mm/icons';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ModalInstanceProps } from '../interfaces';
import { useLandmineOpensHelpers } from '../UniversalUpgradeModal/useLandmineOpensHelpers';
import styles from './CampaignExceedsSendLimitModal.module.css';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import { ExtraSendsSelect } from '../../../dashboard/pages/SettingsPage/BillingPanel/SubscriptionTabsArea/ActiveSubscriptionTab/BillingPageCard/ExtraSendsSelect/ExtraSendsSelect';

type Props = ModalInstanceProps & {
  campaignDisplayType: CampaignDisplayType;
  callToActionType: CallToActionType;
  price?: number;
};

export const CampaignExceedsSendLimitModal = (props: Props) => {
  const { closeModal, campaignDisplayType, callToActionType, price } = props;
  const dispatch: Dispatch<StoreState> = useDispatch();
  const { updateAllotment, sendOveragesEnabled } = useSubscriptionCRUDActions();
  const activeMessagesStatistic = useSelector(activeMessagesStatisticSelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const activeFacebookPagePlanInfo = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).info;
  const defaultProPlan = useSelector(defaultProPlanSelector);
  const monthlyPlansAllotments = useSelector(selectPlansAllotments);
  const annualPlansAllotments = useSelector(selectAnnualPlansAllotments);
  const plans = useSelector(enhancedPlansSelector);

  const plan = plans?.filter(
    plan => plan.planCode === facebookPage?.subscription?.planCode,
  );

  const [activeInput, setActiveInput] = useState(callToActionType);
  const [level, setLevel] = useState(0);
  const [optionsToShow, setOptionsToShow] = useState(false);

  const plansAllotments =
    facebookPage?.subscription?.billingPeriodLength === 'annually'
      ? annualPlansAllotments
      : monthlyPlansAllotments;

  const isCanceledsubscription =
    facebookPage &&
    facebookPage.subscription &&
    facebookPage.subscription.state === 'canceled';

  const { landmineOpenId, landmineOpensCRUDActions } = useLandmineOpensHelpers(
    landmineNames.SENDS_USAGE__EXCEED_SEND,
    defaultProPlan?.name || 'unknown Pro plan',
  );

  const plansAllotmentsOptions = facebookPage
    ? mapPlanAllotmentOptions({
        plansAllotments,
        getExtraAllotment: false,
        page: facebookPage,
        plan,
        useFiltered: true,
        currentSendsLevel: facebookPage?.subscription?.sendAllotmentLevel || 0,
      })
    : [];

  const { november2021Pricing } = useSelector(combinedFeatureFlagsSelector);

  const openBillingModal = (params: OpenBillingModalParams) =>
    dispatch(modalActions.openBillingModal(params));

  const getNoticeText = () => {
    if (isCanceledsubscription) {
      return (
        <>
          <div className={styles.text}>Subscription is canceled.</div>
          <div className={styles.text}>
            Contact support at{' '}
            <Link capitalize={false} href="mailto:support@mobilemonkey.com">
              support@mobilemonkey.com
            </Link>{' '}
            to reactivate your subscription.
          </div>
          <div className={styles.text}>
            Or reduce an audience size and/or the content of this blast.
          </div>
        </>
      );
    }
    switch (callToActionType) {
      case 'enable_overage':
        return (
          <>
            <div className={styles.text}>
              This {campaignDisplayType} will cause your{' '}
              {activeFacebookPagePlanInfo.isAnyTeamPlan
                ? 'Agency subscription'
                : 'page'}{' '}
              to exceed its send credits limit.
            </div>{' '}
            {campaignDisplayType === 'Chat Blast' && price && (
              <div className={styles.creditsText}>
                Remained {activeMessagesStatistic.messagesRemaining} Send
                Credits but required <strong>{price} Send Credits</strong>.
              </div>
            )}
            {campaignDisplayType === 'Drip Campaign' && price && (
              <div className={styles.creditsText}>
                Remained {activeMessagesStatistic.messagesRemaining} Send
                Credits but target audience includes{' '}
                <strong>{price} contacts</strong>.
              </div>
            )}
            <div className={styles.text}>
              <strong>
                Increase send credits limit to continue sending messages
              </strong>
            </div>{' '}
          </>
        );
      case 'upgrade':
      default:
        return `
            This ${campaignDisplayType} will cause your page to exceed its send credits limit.
            Please upgrade to a ${
              november2021Pricing ? 'Startup' : 'Operator'
            } subscription to continue.
          `;
    }
  };
  const getUpgradeButtonCaption = () => {
    switch (callToActionType) {
      case 'enable_overage':
      case 'buy_extra_credits':
        return 'Confirm';
      case 'upgrade':
      default:
        return 'UPGRADE';
    }
  };

  const onUpgradeClick = useCallback(async () => {
    switch (activeInput) {
      case 'enable_overage':
        if (
          facebookPage &&
          facebookPage.subscription &&
          facebookPage.subscription.id
        ) {
          await sendOveragesEnabled(
            facebookPage.subscription.id,
            true,
            facebookPage.id,
          );
        }
        break;
      case 'buy_extra_credits':
        if (
          facebookPage &&
          facebookPage.subscription &&
          facebookPage.subscription.id &&
          optionsToShow
        ) {
          await updateAllotment({
            level,
            subscriptionId: facebookPage.subscription.id,
            companyId: facebookPage.companyId,
            facebookPageId: facebookPage.id,
            loadContactsCount: true,
            fetchActivePage: true,
          });
        }
        break;
      case 'upgrade':
      default:
        if (landmineOpenId) {
          landmineOpensCRUDActions.update(landmineOpenId, true);
        }
        if (defaultProPlan && facebookPage) {
          openBillingModal({
            facebookPage,
            plan: defaultProPlan,
            subscription: facebookPage.subscription,
          });
        }
        break;
    }
    closeModal();
  }, [closeModal, defaultProPlan, facebookPage, openBillingModal]);

  if (!facebookPage) {
    return null;
  }

  return (
    <div className={styles.body}>
      <Heading className={styles.header} size={3}>
        Too many send credits required
        <CloseCircleIcon onClick={closeModal} />
      </Heading>
      <Line />
      {getNoticeText()}
      {callToActionType === 'enable_overage' && !isCanceledsubscription && (
        <div className={styles.inputs}>
          <RadioInput
            className={styles.input}
            checked={activeInput === 'enable_overage'}
            name="overages"
            label="Enable auto-upgrades"
            value="enable_overage"
            onChange={() => setActiveInput('enable_overage')}
          />
          {plansAllotmentsOptions?.length > 0 &&
            campaignDisplayType === 'Chat Blast' && (
              <RadioInput
                className={styles.input}
                checked={activeInput === 'buy_extra_credits'}
                name="credits"
                label="Buy more send credits"
                value="buy_extra_credits"
                onChange={() => setActiveInput('buy_extra_credits')}
              />
            )}
        </div>
      )}
      {activeInput === 'buy_extra_credits' &&
        callToActionType === 'enable_overage' && (
          <ExtraSendsSelect
            page={facebookPage}
            setLevel={setLevel}
            setOptionsToShow={setOptionsToShow}
            plansAllotments={plansAllotments}
            plan={plan}
          />
        )}
      <Line />
      <div className={styles.buttons}>
        <PrimaryButton color="secondary-blue" onClick={closeModal}>
          {isCanceledsubscription ? 'Ok' : 'Cancel'}
        </PrimaryButton>
        {!isCanceledsubscription && (
          <PrimaryButton onClick={onUpgradeClick}>
            {getUpgradeButtonCaption()}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};
